import { useCallback, useMemo, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError, getIsFetching } from '@/reducers/fetch'

import { Alert, Button, Select, Dialog, Text, Label, Loader } from '@/ui'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import {
  ADMIN_ADD_COUPON_KEY,
  adminAddCoupon,
  FETCH_USER_COUPON_TYPES_KEY,
  fetchUserCouponTypes,
} from './adminUserCoupons.actions'
import { UserCoupon } from './userCoupons.slice'
import { buildAssignableCouponOptions } from './adminAssignableCoupons.helpers'

import { TypographyStyles } from '@/styles'

const AdminAddCouponModal = ({
  userId,
  close,
}: {
  userId: number
  close: (newCoupon?: UserCoupon) => void
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string>()
  const userCouponTypes = useFetchResponse(fetchUserCouponTypes)
  const loading = useReselector(getIsFetching, FETCH_USER_COUPON_TYPES_KEY)
  const { options: typeDropDownOptions, productNameByCouponId } = useMemo(
    () => buildAssignableCouponOptions(userCouponTypes),
    [userCouponTypes]
  )
  const [selectedType, setSelectedType] = useState<string>()
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) {
      setError(undefined)
      setSelectedType(undefined)
    }
  }, [])
  const updateError = useReselector(getFetchError, ADMIN_ADD_COUPON_KEY)

  const createCoupon = useCallback(async () => {
    setError(undefined)
    if (!selectedType) {
      setError('Please select a coupon type')
      return false
    }
    const productName = productNameByCouponId.get(selectedType)
    if (!productName) {
      setError('Invalid coupon type')
      return false
    }
    const res = await adminAddCoupon({
      userId,
      couponId: selectedType,
      productName,
    })(dispatch)
    if (res) {
      close(res)
      setOpen(false)
      return true
    }
    return false
  }, [dispatch, selectedType, userId, productNameByCouponId, close])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <Button variant="primary">Add Coupon +</Button>
      </Dialog.Trigger>
      <Dialog.Content onDismiss={() => setOpen(false)}>
        <Dialog.Title>
          <Text variant={TypographyStyles.H2}>
            Add User Coupon for User {userId}
          </Text>
        </Dialog.Title>
        <div className="flex flex-col gap-2">
          <Loader active={loading} />
          <Label>Coupon Type</Label>
          <Select
            withTrigger
            options={typeDropDownOptions}
            value={selectedType}
            onValueChange={(value) => setSelectedType(value)}
            placeholder="Select a coupon"
          />
          {(error || updateError?.message) && (
            <Alert
              variant="error"
              description={error ?? updateError?.message}
            />
          )}
        </div>
        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Dialog.Close>

          <Button variant="primary" onClick={createCoupon}>
            Add
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default AdminAddCouponModal
